import type { FC, ReactNode } from 'react'

import { useRef } from 'react'
import { gsap, MOTION, useGSAP } from '@/lib/motion'
import { BrandUnderline } from '@/components/generals/brand-underline'
import { HouseMark } from '@/components/generals/house-mark'
import { SplitWords } from '@/components/generals/split-words'
import { WHY_CHECKS } from '@/constants/site'

/** Chapter three, on the cyan the manifesto dove into: the promise, then three checks that tick as they arrive. */
export const WhySection: FC = (): ReactNode => {
    const ref = useRef<HTMLElement>(null)

    useGSAP(() => {
        const section = ref.current

        if (!section) return

        const q = gsap.utils.selector(section)
        const mm = gsap.matchMedia()

        mm.add(MOTION.ok, () => {
            gsap.fromTo(q('[data-title] [data-word]'), { yPercent: 110 }, {
                yPercent: 0,
                duration: 1.1,
                stagger: 0.05,
                ease: 'expo.out',
                scrollTrigger: { trigger: q('[data-title]')[0], start: 'top 82%', toggleActions: 'play none none reverse' }
            })

            gsap.fromTo(q('[data-title] [data-underline]'), { strokeDashoffset: 1 }, {
                strokeDashoffset: 0,
                ease: 'none',
                scrollTrigger: { trigger: q('[data-title]')[0], start: 'top 65%', end: 'bottom 40%', scrub: 0.5 }
            })

            gsap.fromTo(q('[data-copy]'), { autoAlpha: 0, y: 40 }, {
                autoAlpha: 1,
                y: 0,
                duration: 1,
                ease: 'expo.out',
                scrollTrigger: { trigger: q('[data-copy]')[0], start: 'top 85%', toggleActions: 'play none none reverse' }
            })

            q('[data-row]').forEach(row => {
                const rule = row.querySelector('[data-rule]')
                const house = row.querySelector('[data-house]')
                const check = row.querySelector('[data-check]')
                const text = row.querySelector('[data-row-text]')
                const index = row.querySelector('[data-row-index]')

                gsap.timeline({
                    scrollTrigger: { trigger: row, start: 'top 82%', toggleActions: 'play none none reverse' }
                })
                    .fromTo(rule, { scaleX: 0 }, { scaleX: 1, duration: 1, ease: 'expo.inOut' }, 0)
                    .fromTo(house, { strokeDashoffset: 1 }, { strokeDashoffset: 0, duration: 0.9, ease: 'power2.inOut' }, 0.15)
                    .fromTo(check, { strokeDashoffset: 1 }, { strokeDashoffset: 0, duration: 0.45, ease: 'power3.out' }, 0.75)
                    .fromTo(text, { yPercent: 105 }, { yPercent: 0, duration: 1, ease: 'expo.out' }, 0.2)
                    .fromTo(index, { autoAlpha: 0 }, { autoAlpha: 1, duration: 0.6 }, 0.5)
            })
        })

        return () => mm.revert()
    }, { scope: ref })

    return (
        <section
            ref={ref}
            id='why-us'
            data-tone='signal'
            data-surface='light'
            aria-labelledby='why-title'
            className='relative bg-signal px-(--gutter) pb-[18vh] pt-[16vh] text-ink'
        >
            <div className='grid gap-x-8 gap-y-8 md:grid-cols-12'>
                <p className='label md:col-span-3 md:pt-4'>
                    <span className='mr-3'>01</span>
                    Why Localboost?
                </p>

                <h2
                    id='why-title'
                    data-title=''
                    aria-label="In short, Localboost will get you better results."
                    className='display text-[clamp(2.7rem,6.8vw,8rem)] leading-[0.9] wdth-87 md:col-span-9'
                >
                    <SplitWords text="In short, Localboost will get you" />{' '}
                    <span className='relative inline-block whitespace-nowrap'>
                        <SplitWords text="better results." />
                        <BrandUnderline className='-bottom-[0.02em] text-ink' />
                    </span>
                </h2>
            </div>

            <div className='mt-[11vh] grid gap-x-8 gap-y-14 md:grid-cols-12'>
                <p
                    data-copy=''
                    className='max-w-[34ch] text-lg leading-relaxed md:col-span-4 md:text-xl'
                >
                    We&apos;ve worked with numerous clients who tried other solutions, but the result is a website with little to no traffic.
                    Your website shouldn&apos;t just be pretty. It should help your business grow.
                </p>

                <ul className='md:col-span-7 md:col-start-6'>
                    {WHY_CHECKS.map((item, index) => (
                        <li
                            key={item}
                            data-row=''
                            className='relative grid grid-cols-[auto_1fr_auto] items-center gap-x-5 py-6 md:gap-x-8 md:py-8'
                        >
                            <span data-rule='' aria-hidden='true' className='absolute left-0 top-0 h-px w-full origin-left bg-ink/30' />

                            <HouseMark variant='outline' className='size-10 md:size-14' />

                            <span className='line-mask'>
                                <span data-row-text='' className='text-[clamp(1.35rem,2.5vw,2.5rem)] font-semibold leading-[1.05] tracking-[-0.025em] wdth-87'>
                                    {item}
                                </span>
                            </span>

                            <span data-row-index='' className='label self-start pt-1'>0{index + 1}</span>
                        </li>
                    ))}
                </ul>
            </div>
        </section>
    )
}
